import React, { useEffect, useState, useCallback } from 'react';
import './InstallPWAButton.css';

/**
 * Install button for PWA installability.
 *
 * Visibility logic:
 *   - If running in standalone (already installed) → hidden forever.
 *   - If the user dismissed recently (7 days) → hidden.
 *   - Otherwise always visible. Behaviour when tapped:
 *       · beforeinstallprompt was captured → trigger native prompt.
 *       · iOS Safari → show "Add to Home Screen" instructions modal.
 *       · Any other browser (no prompt yet, Chrome before heuristics) →
 *         show a generic "install from browser menu" instructions modal.
 *
 * This ensures every visitor sees an obvious way to install the app, even
 * before Chrome decides to fire its native prompt.
 */
const DISMISSAL_KEY = 'ckk_pwa_install_dismissed_until';
const DAY = 24 * 60 * 60 * 1000;

const detectOS = () => {
  const ua = navigator.userAgent || '';
  if (/iPad|iPhone|iPod/.test(ua) && !window.MSStream) return 'ios';
  if (/Android/i.test(ua)) return 'android';
  if (/Macintosh/i.test(ua)) return 'macos';
  if (/Windows/i.test(ua)) return 'windows';
  return 'other';
};

const detectBrowser = () => {
  const ua = navigator.userAgent || '';
  if (/Edg\//.test(ua)) return 'edge';
  if (/Chrome\//.test(ua) && !/Edg\//.test(ua)) return 'chrome';
  if (/Firefox\//.test(ua)) return 'firefox';
  if (/Safari\//.test(ua) && !/Chrome\//.test(ua)) return 'safari';
  if (/SamsungBrowser/i.test(ua)) return 'samsung';
  return 'other';
};

const isStandalone = () =>
  window.matchMedia?.('(display-mode: standalone)').matches ||
  window.navigator.standalone === true;

const InstallPWAButton = ({ variant = 'floating', className = '', onInstalled }) => {
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [showHelp, setShowHelp] = useState(false);
  const [hidden, setHidden] = useState(() => {
    if (isStandalone()) return true;
    try {
      const until = parseInt(localStorage.getItem(DISMISSAL_KEY) || '0', 10);
      return Date.now() < until;
    } catch {
      return false;
    }
  });

  useEffect(() => {
    if (isStandalone()) { setHidden(true); return; }
    const handler = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
    };
    const onInstalledHandler = () => {
      setHidden(true);
      setDeferredPrompt(null);
      if (onInstalled) onInstalled();
    };
    window.addEventListener('beforeinstallprompt', handler);
    window.addEventListener('appinstalled', onInstalledHandler);
    return () => {
      window.removeEventListener('beforeinstallprompt', handler);
      window.removeEventListener('appinstalled', onInstalledHandler);
    };
  }, [onInstalled]);

  const handleInstall = useCallback(async () => {
    if (deferredPrompt) {
      deferredPrompt.prompt();
      const { outcome } = await deferredPrompt.userChoice;
      if (outcome === 'accepted') {
        setHidden(true);
      }
      setDeferredPrompt(null);
      return;
    }
    // Fallback: show manual install instructions
    setShowHelp(true);
  }, [deferredPrompt]);

  const dismiss = useCallback(() => {
    try { localStorage.setItem(DISMISSAL_KEY, String(Date.now() + 7 * DAY)); } catch { /* ignore */ }
    setHidden(true);
  }, []);

  if (hidden) return null;

  const os = detectOS();
  const browser = detectBrowser();

  return (
    <>
      <div
        className={`install-pwa install-pwa-${variant} ${className}`}
        data-testid="install-pwa-container"
      >
        <button
          type="button"
          className="install-pwa-btn"
          onClick={handleInstall}
          data-testid="install-pwa-button"
          aria-label="Install Royal Chess as an app"
        >
          <svg
            className="install-pwa-icon"
            width="16"
            height="16"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2.4"
            strokeLinecap="round"
            strokeLinejoin="round"
            aria-hidden="true"
          >
            <path d="M12 3v12" />
            <path d="M7 10l5 5 5-5" />
            <path d="M4 21h16" />
          </svg>
          <span className="install-pwa-label">Install App</span>
        </button>
        {variant === 'floating' && (
          <button
            type="button"
            className="install-pwa-dismiss"
            onClick={dismiss}
            aria-label="Dismiss install prompt"
            data-testid="install-pwa-dismiss"
          >
            ×
          </button>
        )}
      </div>

      {showHelp && (
        <div
          className="install-pwa-ios-backdrop"
          onClick={() => setShowHelp(false)}
          data-testid="install-pwa-help-modal"
        >
          <div className="install-pwa-ios-modal" onClick={(e) => e.stopPropagation()}>
            <h3>Install Royal Chess</h3>
            <InstallInstructions os={os} browser={browser} />
            <p className="install-pwa-ios-hint">
              Once installed, the app launches fullscreen with its own icon on your home screen — just like any other mobile app.
            </p>
            <button
              type="button"
              className="install-pwa-btn install-pwa-ios-close"
              onClick={() => setShowHelp(false)}
              data-testid="install-pwa-help-close"
            >
              Got it
            </button>
          </div>
        </div>
      )}
    </>
  );
};

const InstallInstructions = ({ os, browser }) => {
  if (os === 'ios') {
    return (
      <ol>
        <li>Open this page in <strong>Safari</strong> (not Chrome).</li>
        <li>Tap the <strong>Share</strong> icon (square with an arrow).</li>
        <li>Scroll and tap <strong>Add to Home Screen</strong>.</li>
        <li>Tap <strong>Add</strong> in the top right.</li>
      </ol>
    );
  }
  if (os === 'android') {
    return (
      <ol>
        <li>Tap the browser's menu <strong>(⋮)</strong> in the top right.</li>
        <li>Choose <strong>Install app</strong> or <strong>Add to Home screen</strong>.</li>
        <li>Confirm by tapping <strong>Install</strong>.</li>
        <li className="install-pwa-hint-tip">If you don't see the option yet, browse the site for ~30 seconds first — Chrome unlocks the install option after some engagement.</li>
      </ol>
    );
  }
  // Desktop
  if (browser === 'chrome' || browser === 'edge') {
    return (
      <ol>
        <li>
          <strong>Easiest path:</strong> open the browser menu <strong>(⋮ top-right)</strong> →{' '}
          {browser === 'chrome'
            ? <><strong>Cast, save, and share</strong> → <strong>Install Royal Chess Kingdom…</strong></>
            : <><strong>Apps</strong> → <strong>Install this site as an app</strong></>}
        </li>
        <li>
          <strong>Or via the address bar:</strong> look at the <strong>right side of the address bar</strong> for a small monitor/download icon (<strong>⊕</strong> in Chrome, <strong>⊞</strong> in Edge). Click it → <strong>Install</strong>.
        </li>
        <li className="install-pwa-hint-tip">
          Don't see the address-bar icon? Either Chrome is waiting for you to engage with the site for ~30 seconds, OR you've installed Royal Chess on this browser before and Chrome is hiding the prompt for a cooldown period. Use the menu path above — it always works.
        </li>
        <li className="install-pwa-hint-tip">
          Already installed and want to launch it? Paste <code>chrome://apps</code> into your address bar.
        </li>
      </ol>
    );
  }
  if (browser === 'safari') {
    return (
      <ol>
        <li>Open <strong>File</strong> menu in the top bar.</li>
        <li>Choose <strong>Add to Dock</strong>.</li>
        <li>Click <strong>Add</strong>.</li>
      </ol>
    );
  }
  return (
    <ol>
      <li>Open your browser's menu.</li>
      <li>Look for <strong>Install app</strong>, <strong>Add to Home screen</strong>, or <strong>Install Royal Chess</strong>.</li>
      <li>Confirm the install prompt.</li>
      <li>Firefox does not currently support PWA installs — try Chrome, Edge, Safari, or Samsung Internet.</li>
    </ol>
  );
};

export default InstallPWAButton;
